import { useContext, useCallback, useState } from "react";
import { SkeletonCircle } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { basePathAuth } from "utils/helpers";
import { navigate } from "@reach/router";
import { SubmissionSmartFieldPreviewContext } from "context/RFQSubmittedResponseContext";
import { SmartFieldCardButtons } from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/SmartFieldCardButtons";
import { cn } from "utils/helpers";
import { SmartFieldKeyPersonnelEditor } from "components/RFQ/SmartFieldEditor/SmartFieldKeyPersonnelEditor";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";

interface KeyPersonnelProject {
	name: string;
}

interface KeyPersonnelValues {
	id: number;
	avatar_location?: string;
	experience?: number;
	first_name?: string;
	last_name?: string;
	position?: string;
	projects_completed?: KeyPersonnelProject[];
}

interface KeyPersonnelSkeletonProps {
	rfqSubmission?: boolean;
}

interface KeyPersonnelProps {
	className?: string;
	companyId: number;
	isClientPreview?: boolean;
	isPreview?: boolean;
	isSubmitting?: boolean;
	rfqSubmission?: boolean;
	selected?: boolean;
	supplierSmartField?: boolean;
	values: KeyPersonnelValues;
	selectHandler?: (id: number) => void;
}

export const KeyPersonnelSkeleton: React.FC<KeyPersonnelSkeletonProps> = ({ rfqSubmission }) => {
	return (
		<div className="smart-field-scroll my-3 min-w-[410px] animate-pulse overflow-hidden rounded-md bg-gray-100 pb-6">
			<div className="space-y-6 px-6 py-2">
				<div className="mb-3 mt-5 flex items-center justify-start gap-3">
					<SkeletonCircle
						size="56px"
						startColor="lightgray"
					/>
					<SkeletonBase width="160px" />
				</div>
				<div className="space-y-4 pb-3 pt-3">
					<SkeletonBase width="100%" />
					<SkeletonBase width="100%" />
					<SkeletonBase width="100%" />
				</div>
				{rfqSubmission && (
					<SkeletonBase
						width="100%"
						height="46px"
					/>
				)}
			</div>
		</div>
	);
};

export const KeyPersonnel: React.FC<KeyPersonnelProps> = ({
	values,
	rfqSubmission,
	selected,
	selectHandler,
	companyId,
	isPreview,
	supplierSmartField,
	isSubmitting,
	className,
	isClientPreview,
}) => {
	const { rfxId } = useRfxSubmissionData();
	const submissionSmartFieldContext = useContext(
		SubmissionSmartFieldPreviewContext
	);
	const [isEditorOpen, setIsEditorOpen] = useState(false);

	const handleViewClick = useCallback(() => {
		submissionSmartFieldContext.setModalDetail({
			data: values,
			type: "resource",
		});
		submissionSmartFieldContext.setIsModalOpen(true);
	}, [submissionSmartFieldContext, values]);

	const handleKeyPersonnelOpen = (): void => {
		if (supplierSmartField && rfxId) {
			setIsEditorOpen(true);
		} else {
			navigate(
				`${basePathAuth(companyId)}company-profile/resources/edit-resource/${values?.id}`
			);
		}
	};

	return (
		<>
			<div
				className={cn(
					"smart-field-scroll my-3 h-max min-w-[410px] overflow-hidden rounded-md bg-white pb-3 shadow-md",
					className
				)}
			>
				<div
					className="relative flex items-center justify-between"
					style={{ height: "6px" }}
				>
					<div className="absolute left-0 top-0 h-[6px] w-full bg-royal-blue2"></div>
				</div>
				<div className="space-y-6 px-6">
					<div className="mb-3 mt-5 flex items-center justify-start">
						<div className="">
							<div
								className="rounded-full"
								style={{
									width: "56px",
									height: "56px",
									backgroundImage: `url(${values?.avatar_location || "/img/user-01.svg"})`,
									backgroundSize: "cover",
								}}
							></div>
						</div>
						<div
							className="ml-4 font-roboto font-semibold capitalize text-tertiary-900"
							style={{ fontSize: "20px", lineHeight: "30px" }}
						>
							{(values?.first_name || "") + "  " + (values?.last_name || "")}
						</div>
					</div>
					<div className="pb-3 pt-1">
						<div
							className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Position
							</div>
							<div className="font-roboto text-[14px] font-semibold text-primary-900">
								{values?.position || " - "}
							</div>
						</div>
						<div
							className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Industry experience
							</div>
							<div className="font-roboto text-[14px] font-semibold text-primary-900">
								{values?.experience || 0} years
							</div>
						</div>
						<div
							className="flex items-center justify-between space-x-0"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Most Recent Project
							</div>
							<div className="line-clamp-1 max-w-[50%] font-roboto text-[14px] font-semibold text-primary-900">
								{values?.projects_completed?.length > 0
									? values?.projects_completed[0]?.name
									: " - "}
							</div>
						</div>
					</div>
					{!isClientPreview && (
						<SmartFieldCardButtons
							rfqSubmission={rfqSubmission}
							selected={selected}
							selectHandler={() => selectHandler(values?.id)}
							isLoading={isSubmitting}
							editHandler={handleKeyPersonnelOpen}
							editLabel="Key Personnel"
							isPreview={isPreview}
							viewHandler={handleViewClick}
							supplierSmartField={supplierSmartField}
							dataTestId={`select-key-personnel-${values?.id}`}
						/>
					)}
				</div>
			</div>
			{isEditorOpen && (
				<Modal
					isOpen={isEditorOpen}
					onClose={() => setIsEditorOpen(false)}
					headerContent={`Edit ${values?.first_name} ${values?.last_name}`}
				>
					<SmartFieldKeyPersonnelEditor
						keyPersonnelId={values.id}
						rfxId={rfxId}
						companyId={companyId}
						onRequestClose={() => setIsEditorOpen(false)}
					/>
				</Modal>
			)}
		</>
	);
};
